import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Navbar from '../../components/navbar/navbar';
import Footer from '../../components/footer';
import { API_BASE, getImageUrl } from '../../utils/api';
import defaultCityImg from '../../assets/img/locations/kudasan.png';

interface Locality {
    _id: string;
    name: string;
    slug: string;
    image: string;
    propertyCount: number;
    projectCount: number;
    totalCount: number;
    cityName: string;
}

const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
        opacity: 1,
        transition: {
            staggerChildren: 0.1
        }
    }
};

const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
};

export default function Localities() {
    const [locations, setLocations] = useState<Locality[]>([]);
    const [loading, setLoading] = useState(true);
    const [selectedCity, setSelectedCity] = useState('All Cities');
    const [searchTerm, setSearchTerm] = useState('');
    const [visibleRows, setVisibleRows] = useState(5);
    const [itemsPerRow, setItemsPerRow] = useState(5);

    useEffect(() => {
        const getItemsPerRow = () => {
            if (typeof window === 'undefined') return 5;
            const width = window.innerWidth;
            if (width >= 1280) return 5; // xl
            if (width >= 1024) return 4; // lg
            if (width >= 768) return 3;  // md
            return 2;                    // mobile/tablet view
        };

        const handleResize = () => {
            setItemsPerRow(getItemsPerRow());
        };

        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        fetch(`${API_BASE}/localities/with-counts`)
            .then(r => r.json())
            .then(data => {
                if (Array.isArray(data)) {
                    setLocations(data);
                }
            })
            .catch(console.error)
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => {
        setVisibleRows(5);
    }, [selectedCity, searchTerm]);

    const cities = Array.from(new Set(locations.map(loc => loc.cityName).filter(Boolean)));

    const filteredLocations = locations.filter(loc => {
        const matchesCity = selectedCity === 'All Cities' || loc.cityName === selectedCity;
        const matchesSearch = loc.name.toLowerCase().includes(searchTerm.toLowerCase());
        return matchesCity && matchesSearch;
    });

    const displayLocations = filteredLocations.slice(0, visibleRows * itemsPerRow);

    return (
        <>
            <Navbar transparent={false} />

            <div className="bg-[#f4f6f9] min-h-screen pt-4 md:pt-6 pb-12 md:pb-16">
                <div className="container mx-auto px-3 md:px-4">
                    
                    {/* BREADCRUMB / HEADER */}
                    <div className="mb-6 md:mb-8 text-center">
                        <h1 className="text-2xl md:text-5xl font-black text-[#05144b] mb-3 md:mb-4">
                            Explore <span className="text-yellow-500">Locations</span>
                        </h1>
                        <div className="w-16 md:w-24 h-1 md:h-1.5 bg-yellow-500 mx-auto rounded-full mb-4 md:mb-6"></div>
                        <p className="text-gray-600 max-w-2xl mx-auto font-medium text-xs md:text-base px-2">
                            Discover the best neighborhoods and find your perfect property in these top-rated locations.
                        </p>
                    </div>

                    {/* SEARCH OPTION */}
                    <div className="max-w-2xl mx-auto mb-8 md:mb-10 px-2">
                        <div className="relative group">
                            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                <i className="fa-solid fa-magnifying-glass text-gray-400 group-focus-within:text-[#05144b] transition-colors"></i>
                            </div>
                            <input
                                type="text"
                                placeholder="Search by locality name..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="block w-full pl-11 pr-4 py-3 md:py-4 bg-white border border-gray-200 rounded-2xl md:rounded-[1.5rem] text-sm md:text-base font-medium text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#05144b]/10 focus:border-[#05144b] shadow-sm transition-all duration-300"
                            />
                            {searchTerm && (
                                <button
                                    onClick={() => setSearchTerm('')}
                                    className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-400 hover:text-gray-600 transition-colors"
                                >
                                    <i className="fa-solid fa-circle-xmark"></i>
                                </button>
                            )}
                        </div>
                    </div>

                    {/* CITY FILTER */}
                    {!loading && cities.length > 0 && (
                        <div className="flex flex-nowrap md:flex-wrap overflow-x-auto md:overflow-visible justify-start md:justify-center gap-2 md:gap-3 mb-6 md:mb-10 pb-3 md:pb-0 px-1 md:px-0 scrollbar-none">
                            <button
                                onClick={() => setSelectedCity('All Cities')}
                                className={`flex-shrink-0 px-4 md:px-6 py-2 md:py-2.5 rounded-full text-[10px] md:text-xs font-black transition-all duration-300 shadow-sm border ${
                                    selectedCity === 'All Cities'
                                        ? 'bg-gradient-to-r from-[#05144b] to-[#4c1d95] text-white border-transparent scale-105 shadow-md'
                                        : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                                }`}
                            >
                                All Cities
                            </button>
                            {cities.map((city) => (
                                <button
                                    key={city}
                                    onClick={() => setSelectedCity(city)}
                                    className={`flex-shrink-0 px-4 md:px-6 py-2 md:py-2.5 rounded-full text-[10px] md:text-xs font-black transition-all duration-300 shadow-sm border ${
                                        selectedCity === city
                                            ? 'bg-gradient-to-r from-[#05144b] to-[#4c1d95] text-white border-transparent scale-105 shadow-md'
                                            : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                                    }`}
                                >
                                    {city}
                                </button>
                            ))}
                        </div>
                    )}

                    {loading ? (
                        <div className="text-center py-20">
                            <div className="w-12 h-12 border-4 border-[#05144b] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                            <p className="text-[#05144b] font-bold text-xs">Mapping Neighborhoods...</p>
                        </div>
                    ) : (
                        <>
                            <motion.div 
                                className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 md:gap-6"
                                variants={containerVariants}
                                initial="hidden"
                                animate="visible"
                            >
                                {displayLocations.map((loc) => (
                                    <motion.div
                                        key={loc._id}
                                        className="group relative aspect-[3/4] rounded-2xl md:rounded-3xl overflow-hidden bg-slate-900 border border-white/10 cursor-pointer shadow-xl transition-all duration-500 hover:-translate-y-1 hover:shadow-2xl"
                                        variants={itemVariants}
                                    >
                                        <Link to={`/location/${loc.slug || loc.name.toLowerCase().replace(/ /g, '-')}`} className="block h-full w-full">
                                            <div className="relative h-full w-full">
                                                <img
                                                    src={loc.image ? getImageUrl(loc.image) : defaultCityImg}
                                                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-1000 group-hover:scale-110 brightness-[0.7] group-hover:brightness-90"
                                                    alt={loc.name}
                                                />
                                                
                                                {/* Gradients */}
                                                <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-black/90 via-black/40 to-transparent z-10"></div>

                                                <div className="absolute inset-x-0 bottom-0 text-center pb-4 md:pb-6 px-2 md:px-4 z-20 transition-transform duration-500 group-hover:translate-y-[-5px]">
                                                    <h5 className="text-[13px] md:text-[18px] font-black text-white mb-1 md:mb-2 drop-shadow-lg truncate">
                                                        {loc.name}
                                                    </h5>
                                                    <div className="inline-block">
                                                        <span className="bg-gradient-to-r from-[#05144b] to-[#4c1d95] text-white text-[8px] md:text-[11px] font-black px-2.5 py-1 md:px-4 md:py-1.5 rounded-full shadow-lg">
                                                            {loc.projectCount || 0} Projects
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        </Link>
                                    </motion.div>
                                ))}
                            </motion.div>

                            {filteredLocations.length > displayLocations.length && (
                                <div className="text-center mt-8 md:mt-12">
                                    <button
                                        onClick={() => setVisibleRows(prev => prev + 5)}
                                        className="inline-flex bg-gradient-to-r from-[#05144b] to-[#4c1d95] text-white px-6 md:px-8 py-2.5 md:py-3 rounded-xl font-black text-xs md:text-sm hover:brightness-110 transition-all duration-300 shadow-xl items-center gap-3 group"
                                    >
                                        View More Locations
                                        <i className="fa-solid fa-arrow-down text-xs group-hover:translate-y-1 transition-transform"></i>
                                    </button>
                                </div>
                            )}
                        </>
                    )}

                    {!loading && filteredLocations.length === 0 && (
                        <div className="text-center py-20 bg-white rounded-[2rem] shadow-sm border border-dashed border-gray-200 mt-8">
                            <i className="fa-solid fa-map-pin text-4xl text-gray-200 mb-4 block"></i>
                            <p className="text-gray-400 font-bold text-sm">No locations discovered yet</p>
                        </div>
                    )}
                </div>
            </div>

            <Footer />
        </>
    );
}
